import { supabase } from '../../supabase';
import { ensureGymId, getCachedGymId } from '../receptionist/gymContext';
import { RECEPTIONIST_SALE_PAGES } from '../../pages/receptionist/sales/salePageConfig';
import { formatGymPrice } from '../../utils/gymMoney';
import { dashboardTodayKey } from './dashboardSaleMetrics';

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function dayLabel(iso, count) {
  const d = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return d || '—';
  const date = new Date(`${d}T00:00:00`);
  if (count > 14) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

function monthLabel(iso) {
  const d = String(iso || '').slice(0, 10);
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short' });
}

function seriesDays(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const labels = list.map((row, _, arr) => dayLabel(row.d, arr.length));
  const values = list.map((row) => num(row.v));
  return { labels, values };
}

function seriesMonths(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    labels: list.map((row) => monthLabel(row.m)),
    values: list.map((row) => num(row.v)),
  };
}

function isRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return (
    msg.includes('could not find the function')
    || msg.includes('function public.get_dashboard_overview')
    || msg.includes('schema cache')
    || (msg.includes('get_dashboard_overview') && msg.includes('does not exist'))
  );
}

export function emptyDashboardOverview(scopeLabel = 'All branches') {
  return {
    stats: [],
    charts: {
      revenue: { labels: [], values: [] },
      attendance: { labels: [], values: [] },
      renewals: { labels: [], values: [] },
      registrations: { labels: [], values: [] },
      invoices: { labels: [], values: [] },
      visitors: { labels: [], values: [] },
      finance: { labels: [], income: [], expenses: [] },
      categorySales: { labels: [], values: [] },
      dailyCollection: { labels: [], values: [] },
      salesByDay: { labels: [], values: [] },
      membershipVsRenewal: { labels: [], values: [] },
      subscriptionTrend: { labels: [], values: [] },
      registrationTrend: { labels: [], values: [] },
    },
    scopeLabel,
    fromRpc: true,
  };
}

/**
 * Map get_dashboard_overview JSON → existing Dashboard { stats, charts }.
 * @param {object} data
 * @param {{ panel?: 'admin' | 'receptionist', scopeLabel?: string, filtered?: boolean }} opts
 */
export function mapDashboardRpcToOverview(data, opts = {}) {
  if (!data || data.error) return null;
  const panel = opts.panel === 'receptionist' ? 'receptionist' : 'admin';
  const filtered = Boolean(opts.filtered ?? data.filtered);
  const scopeLabel = opts.scopeLabel
    || (panel === 'receptionist' ? 'This branch' : 'All branches');
  const counts = data.counts || {};
  const sales = data.sales || {};
  const chartsIn = data.charts || {};
  const rangeSuffix = filtered ? 'range' : 'month';
  const windowHint = filtered
    ? `${scopeLabel} · selected dates`
    : `${scopeLabel} · current calendar month`;

  const bySource = new Map(
    (Array.isArray(sales.by_source) ? sales.by_source : []).map((row) => [
      String(row.id || ''),
      { count: num(row.count), amount: num(row.amount) },
    ]),
  );

  const saleStats = RECEPTIONIST_SALE_PAGES.map((page) => {
    const row = bySource.get(page.source) || { count: 0, amount: 0 };
    return {
      key: page.source,
      label: filtered ? `${page.label} (range)` : `${page.label} (month)`,
      value: String(row.count),
      change: row.amount > 0 ? formatGymPrice(row.amount) : 'Rs 0',
      variant: 'success',
    };
  });

  const newMembershipCount = num(sales.new_membership_count);
  const newMembershipAmount = num(sales.new_membership_amount);
  const registrationFee = num(sales.registration_fee);
  const guestCount = num(sales.guest_count);
  const guestAmount = num(sales.guest_amount);
  const revenue = num(sales.revenue);
  const paymentsCount = num(sales.payments_count);
  const remainingCount = num(sales.remaining_count);
  const memberCount = num(counts.members);
  const attendanceCount = num(counts.attendance);
  const freezeCount = num(counts.freezes);
  const activeMemberships = num(counts.active_memberships);
  const expiringSoon = num(counts.expiring_soon);

  const newMembershipStat = {
    key: 'newMembership',
    label: filtered ? 'New Membership (range)' : 'New Membership (month)',
    value: String(newMembershipCount),
    change: formatGymPrice(newMembershipAmount),
    variant: 'success',
  };
  const newMembershipAmountStat = {
    key: 'newMembershipAmount',
    label: filtered ? 'New Membership Amount (range)' : 'New Membership Amount (month)',
    value: formatGymPrice(newMembershipAmount),
    change: `${newMembershipCount} sale${newMembershipCount === 1 ? '' : 's'}`,
    variant: 'success',
  };
  const registrationFeeStat = {
    key: 'registrationFee',
    label: filtered ? 'Registration Fee (range)' : 'Registration Fee (month)',
    value: formatGymPrice(registrationFee),
    change: 'From new memberships',
    variant: 'info',
  };
  const guestPassStat = {
    key: 'guestPass',
    label: filtered ? 'Guest Pass (range)' : 'Guest Pass (month)',
    value: String(guestCount),
    change: guestAmount > 0 ? formatGymPrice(guestAmount) : 'Rs 0',
    variant: 'info',
  };
  const revenueStat = {
    key: panel === 'admin' ? 'revenue' : 'collection',
    label: filtered ? 'Total Revenue (range)' : 'Total Revenue (month)',
    value: formatGymPrice(revenue),
    change: windowHint,
    variant: 'success',
  };
  const paymentsCountStat = {
    key: 'payments',
    label: filtered ? 'Payments (range)' : 'Payments (month)',
    value: String(paymentsCount),
    change: revenue ? formatGymPrice(revenue) : '',
  };

  const dailyCollection = seriesDays(chartsIn.daily_collection);
  const salesByDay = seriesDays(chartsIn.sales_by_day);
  const attendance = seriesDays(chartsIn.attendance_by_day);
  const visitors = seriesDays(chartsIn.visitors_by_day);
  const registrationTrend = seriesDays(chartsIn.registrations_by_day);
  const subscriptionTrend = seriesDays(chartsIn.subscription_by_day);
  const revenueMonths = seriesMonths(chartsIn.revenue_by_month);
  const registrationMonths = seriesMonths(chartsIn.registrations_by_month);
  const renewalMonths = seriesMonths(chartsIn.renewals_by_month);
  const categoryRows = Array.isArray(chartsIn.category_sales) ? chartsIn.category_sales : [];
  const vs = chartsIn.membership_vs_renewal || {};

  const charts = {
    revenue: revenueMonths,
    attendance,
    renewals: renewalMonths,
    registrations: registrationMonths,
    invoices: dailyCollection,
    visitors,
    finance: {
      labels: revenueMonths.labels,
      income: revenueMonths.values,
      expenses: revenueMonths.values.map(() => 0),
    },
    categorySales: {
      labels: categoryRows.map((row) => row.label || row.id),
      values: categoryRows.map((row) => num(row.value)),
    },
    dailyCollection,
    salesByDay,
    membershipVsRenewal: {
      labels: ['New Membership', 'Renewal'],
      values: [num(vs.new_membership), num(vs.renewal)],
    },
    subscriptionTrend,
    registrationTrend,
  };

  const remainingStat = {
    key: 'remaining',
    label: 'Remaining Payments (open)',
    value: String(remainingCount),
    change: `${scopeLabel} · outstanding balance`,
    variant: remainingCount ? 'warning' : undefined,
  };

  let stats;
  if (panel === 'receptionist') {
    stats = [
      {
        key: 'members',
        label: filtered ? 'New Members (range)' : 'Total Members',
        value: String(memberCount),
        change: filtered ? scopeLabel : `${scopeLabel} · all-time`,
        variant: 'primary',
      },
      newMembershipStat,
      newMembershipAmountStat,
      registrationFeeStat,
      {
        key: 'memberships',
        label: 'Active Memberships',
        value: String(activeMemberships),
        change: scopeLabel,
        variant: 'success',
      },
      {
        key: 'attendance',
        label: filtered ? 'Attendance (range)' : "Today's Attendance",
        value: String(attendanceCount),
        change: filtered ? scopeLabel : `${scopeLabel} · today`,
        variant: 'warning',
      },
      {
        key: 'classes',
        label: 'Classes',
        value: String(num(counts.classes)),
        change: 'Gym-wide',
        variant: 'primary',
      },
      guestPassStat,
      ...saleStats,
      remainingStat,
      {
        key: 'freeze',
        label: filtered ? 'Freezes (range)' : 'Active Freezes',
        value: String(freezeCount),
        change: filtered ? scopeLabel : `${scopeLabel} · active now`,
        variant: 'info',
      },
      revenueStat,
      paymentsCountStat,
      {
        key: 'trainers',
        label: 'Trainers',
        value: String(num(counts.trainers)),
        change: `${scopeLabel} · active`,
        variant: 'success',
      },
      {
        key: 'equipments',
        label: 'Equipment',
        value: String(num(counts.equipments)),
        change: 'Gym-wide',
        variant: 'warning',
      },
      {
        key: 'lockers',
        label: 'Assigned Lockers',
        value: String(num(counts.assigned_lockers)),
        change: `${scopeLabel} · assigned`,
        variant: 'primary',
      },
    ];
  } else {
    stats = [
      {
        label: filtered ? 'New Members (range)' : 'Total Members',
        value: String(memberCount),
        change: filtered ? scopeLabel : `${scopeLabel} · all-time`,
        key: 'members',
      },
      newMembershipStat,
      newMembershipAmountStat,
      registrationFeeStat,
      { label: 'Packages', value: String(num(counts.packages)), change: 'Gym-wide', key: 'packages' },
      {
        label: 'Active Memberships',
        value: String(activeMemberships),
        change: expiringSoon
          ? `${scopeLabel} · ${expiringSoon} expiring soon`
          : scopeLabel,
        key: 'memberships',
        variant: expiringSoon ? 'warning' : undefined,
      },
      {
        label: 'Trainers',
        value: String(num(counts.trainers)),
        change: `${scopeLabel} · active`,
        key: 'trainers',
      },
      {
        label: 'Receptionists',
        value: String(num(counts.receptionists)),
        change: 'Gym-wide · active',
        key: 'receptionists',
      },
      {
        label: filtered ? 'Attendance (range)' : "Today's Attendance",
        value: String(attendanceCount),
        change: filtered ? scopeLabel : `${scopeLabel} · today`,
        key: 'attendance',
      },
      revenueStat,
      paymentsCountStat,
      guestPassStat,
      { label: 'Classes', value: String(num(counts.classes)), change: 'Gym-wide', key: 'classes' },
      { label: 'Equipment', value: String(num(counts.equipments)), change: 'Gym-wide', key: 'equipments' },
      {
        label: 'Assigned Lockers',
        value: String(num(counts.assigned_lockers)),
        change: counts.lockers ? `${scopeLabel} · ${num(counts.lockers)} total` : scopeLabel,
        key: 'lockers',
      },
      {
        label: filtered ? 'Freezes (range)' : 'Active Freezes',
        value: String(freezeCount),
        change: filtered ? scopeLabel : `${scopeLabel} · active now`,
        key: 'freeze',
      },
      ...saleStats,
      remainingStat,
      { label: 'Payment Methods', value: '—', change: 'Configure in settings', key: 'paymentMethods' },
    ];
  }

  return {
    stats,
    charts,
    scopeLabel,
    fromRpc: true,
    rangeSuffix,
    windowHint,
  };
}

/**
 * One-shot dashboard RPC. Returns null when the function is not deployed yet.
 */
export async function fetchDashboardOverviewRpc({
  startDate = '',
  endDate = '',
  branch = '',
  panel = 'admin',
  scopeLabel = '',
} = {}) {
  const gid = getCachedGymId() || await ensureGymId().catch(() => '');
  if (!gid) return null;

  const from = String(startDate || '').slice(0, 10);
  const to = String(endDate || '').slice(0, 10);
  const today = dashboardTodayKey();
  const branchValue = String(branch || '').trim();
  const pBranch = !branchValue || branchValue === 'all' ? null : branchValue;

  const receptionist = panel === 'receptionist';
  const { data, error } = await supabase.rpc('get_dashboard_overview', {
    p_gym_id: gid,
    p_date_from: /^\d{4}-\d{2}-\d{2}$/.test(from) ? from : null,
    p_date_to: /^\d{4}-\d{2}-\d{2}$/.test(to) ? to : null,
    p_branch: pBranch,
    p_today: today,
    p_receptionist: receptionist,
  });

  if (error) {
    if (isRpcMissing(error)) return null;
    throw error;
  }

  return mapDashboardRpcToOverview(data, {
    panel,
    scopeLabel,
    filtered: receptionist ? true : Boolean(from || to),
  });
}
